import {
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useViewportSize } from '@mantine/hooks';

import {
  allocateAssistantStack,
  type AssistantStackAllocation,
  clampAssistantRailWidth,
  getDefaultAssistantPanelWidth,
  getDefaultSourcesPanelWidth,
  resolvePackDesiredWidths,
  resolveStoredSourcesWidth,
} from './assistantPanelWidth';
import {
  AI_ASSISTANT_PANEL_WIDTH_VAR,
  AI_ASSISTANT_RAIL_WIDTH_VAR,
  DEFAULT_ASSISTANT_RAIL_WIDTH,
} from './constants';
import { usePanelResize } from './usePanelResize';

type UseAssistantPanelWidthOptions = {
  documentOpen: boolean;
  isRailOpenEnabled: boolean;
  sourcesOpen: boolean;
};

type UseAssistantPanelWidthResult = {
  hideRail: () => void;
  isRailOpen: boolean;
  isRailSplit: boolean;
  isResizing: boolean;
  /** Measured outer width (includes the chat rail when open). */
  layoutPanelWidth: number;
  panelRef: RefObject<HTMLElement | null>;
  panelStyle: CSSProperties;
  setSourcesWidth: (width: number) => void;
  sourcesWidth: number;
  startResizeOuter: (event: ReactPointerEvent<HTMLElement>) => void;
  startResizeSplit: (event: ReactPointerEvent<HTMLElement>) => void;
  toggleRail: () => void;
};

type StackSnapshot = {
  chat: number;
  documentOpen: boolean;
  rail: number;
  railOpen: boolean;
  sources: number;
  sourcesOpen: boolean;
};

/**
 * Manages the AI assistant chat, rail, and sources widths as one stack.
 *
 * - {@link panelStyle} sets `--ai-assistant-panel-width` and
 *   `--ai-assistant-rail-width`.
 * - Viewport changes and document/sources open flags re-pack via
 *   `allocateAssistantStack` so the stack never overflows.
 * - When the rail is open beside a chat, the outer-left handle resizes the
 *   rail (chat width stays put until the 80% cap) and the split handle
 *   reallocates width between rail and chat.
 */
export function useAssistantPanelWidth({
  documentOpen,
  isRailOpenEnabled,
  sourcesOpen,
}: UseAssistantPanelWidthOptions): UseAssistantPanelWidthResult {
  const panelRef = useRef<HTMLElement>(null);
  const { width: viewportWidth } = useViewportSize();
  const [isRailOpen, setIsRailOpen] = useState(false);
  const [railWidth, setRailWidth] = useState(DEFAULT_ASSISTANT_RAIL_WIDTH);
  const [panelWidth, setPanelWidth] = useState(getDefaultAssistantPanelWidth);
  const [sourcesWidth, setSourcesWidthState] = useState(
    getDefaultSourcesPanelWidth
  );
  const isRailSplit = isRailOpen && isRailOpenEnabled && !documentOpen;
  const preferredRef = useRef({
    chat: panelWidth,
    sources: sourcesWidth,
  });
  const wasDocumentOpenRef = useRef(documentOpen);

  const stackRef = useRef<StackSnapshot>({
    chat: panelWidth,
    documentOpen,
    rail: railWidth,
    railOpen: isRailOpen,
    sources: sourcesWidth,
    sourcesOpen,
  });
  stackRef.current = {
    chat: panelWidth,
    documentOpen,
    rail: railWidth,
    railOpen: isRailOpen && isRailOpenEnabled,
    sources: sourcesWidth,
    sourcesOpen,
  };

  const applyPacked = useCallback((
    packed: AssistantStackAllocation,
    desiredSources: number,
    sourcesOpen: boolean
  ) => {
    setPanelWidth((current) => (current === packed.chat ? current : packed.chat));
    setRailWidth((current) =>
      packed.railOpen && current !== packed.rail ? packed.rail : current
    );
    setSourcesWidthState((current) =>
      resolveStoredSourcesWidth({
        current,
        desiredWhenClosed: desiredSources,
        packedSources: packed.sources,
        sourcesOpen,
      })
    );
    setIsRailOpen((current) => {
      if (documentOpen) {
        return false;
      }

      // List view packs without a rail, but that is not the user closing it.
      if (!isRailOpenEnabled) {
        return current;
      }

      return current === packed.railOpen ? current : packed.railOpen;
    });
  }, [documentOpen, isRailOpenEnabled]);

  const pack = useCallback(
    (overrides: Partial<StackSnapshot> = {}) => {
      const current = stackRef.current;
      const viewport = viewportWidth || globalThis.window.innerWidth;
      const nextDocumentOpen = overrides.documentOpen ?? current.documentOpen;
      const closingDocument = wasDocumentOpenRef.current && !nextDocumentOpen;
      const preferred = preferredRef.current;
      const desired = resolvePackDesiredWidths({
        closingDocument,
        currentChat: current.chat,
        currentSources: current.sources,
        overrideChat: overrides.chat,
        overrideSources: overrides.sources,
        preferredChat: preferred.chat,
        preferredSources: preferred.sources,
      });
      const nextSourcesOpen = overrides.sourcesOpen ?? current.sourcesOpen;
      const packed = allocateAssistantStack({
        chat: desired.chat,
        documentOpen: nextDocumentOpen,
        rail: overrides.rail ?? current.rail,
        railOpen: overrides.railOpen ?? current.railOpen,
        sources: desired.sources,
        sourcesOpen: nextSourcesOpen,
        viewport,
      });

      applyPacked(packed, desired.sources, nextSourcesOpen);
      wasDocumentOpenRef.current = nextDocumentOpen;

      if (overrides.chat != null) {
        preferredRef.current.chat = overrides.chat;
      }
      if (overrides.sources != null) {
        preferredRef.current.sources = overrides.sources;
      }

      if (!nextDocumentOpen) {
        preferredRef.current = {
          chat: packed.chat,
          sources:
            packed.sources > 0 ? packed.sources : preferredRef.current.sources,
        };
      }
    },
    [applyPacked, viewportWidth]
  );

  useLayoutEffect(() => {
    const viewport = viewportWidth || globalThis.window.innerWidth;
    if (!viewport) {
      return;
    }

    pack();
  }, [documentOpen, isRailOpen, isRailOpenEnabled, pack, sourcesOpen, viewportWidth]);

  const { isResizing: isResizingOuter, startResize: startResizeOuter } =
    usePanelResize({
      getWidth: (clientX) => globalThis.window.innerWidth - clientX,
      onResize: (nextWidth) => {
        const current = stackRef.current;
        if (current.railOpen) {
          pack({ rail: nextWidth - current.chat });
          return;
        }

        pack({ chat: nextWidth, railOpen: false });
      },
    });

  const { isResizing: isResizingSplit, startResize: startResizeSplit } =
    usePanelResize({
      getWidth: (clientX) => {
        const left = panelRef.current?.getBoundingClientRect().left ?? 0;
        return clientX - left;
      },
      onResize: (nextWidth) => {
        const current = stackRef.current;
        const totalWidth = current.chat + current.rail;
        const nextRailWidth = clampAssistantRailWidth(nextWidth, totalWidth);
        pack({
          chat: totalWidth - nextRailWidth,
          rail: nextRailWidth,
          railOpen: true,
        });
      },
    });

  const hideRail = useCallback(() => {
    setIsRailOpen(false);
  }, []);

  const toggleRail = useCallback(() => {
    if (documentOpen) {
      return;
    }

    setIsRailOpen((railOpen) => !railOpen);
  }, [documentOpen]);

  const setSourcesWidth = useCallback(
    (width: number) => {
      pack({ sources: width, sourcesOpen: true });
    },
    [pack]
  );

  const layoutPanelWidth = panelWidth + (isRailSplit ? railWidth : 0);

  const panelStyle = {
    [AI_ASSISTANT_PANEL_WIDTH_VAR]: `${panelWidth}px`,
    [AI_ASSISTANT_RAIL_WIDTH_VAR]: `${railWidth}px`,
  } as CSSProperties;

  return {
    hideRail,
    isRailOpen: isRailSplit,
    isRailSplit,
    isResizing: isResizingOuter || isResizingSplit,
    layoutPanelWidth,
    panelRef,
    panelStyle,
    setSourcesWidth,
    sourcesWidth,
    startResizeOuter,
    startResizeSplit,
    toggleRail,
  };
}
